import { rgb, type Color, type PDFDocument, type PDFPage } from "pdf-lib";
import { PdfFontSet, replacedCharsMessage, type EmbeddedSans, type RasterEnv } from "@/tools/pdf/pdfFonts";

/**
 * Text drawing for the document generators (invoice, receipt, legal documents).
 *
 * pdf-lib's built-in Helvetica can only draw the 218 WinAnsi characters, and drawing anything else
 * (₹, Ł, ő, ğ, Hindi, Chinese, Arabic, emoji) used to throw, so the download silently did nothing.
 * This wraps the site's shared PdfFontSet: WinAnsi text uses Helvetica, Latin-extended / Greek / Cyrillic /
 * ₹ use an embedded DejaVu Sans, and other scripts are drawn by the browser as pictures.
 */
export class TextKit {
  private constructor(readonly set: PdfFontSet, private readonly emoji: string[] = []) {}

  /** `allText` is everything that will be drawn, so the right fonts are loaded up front. */
  static async create(pdf: PDFDocument, allText: string, rasterEnv?: RasterEnv | null, embedSans: EmbeddedSans | null = null): Promise<TextKit> {
    // Devanagari goes through the browser (drawn as pictures, shaped correctly) rather than the embedded font, whose conjuncts render wrongly.
    // `embedSans` embeds the Latin text's fonts instead of using the built-in Helvetica (certificates, for print shops).
    const set = rasterEnv === undefined ? new PdfFontSet(pdf, undefined, undefined, null, embedSans) : new PdfFontSet(pdf, undefined, rasterEnv, null, embedSans);
    await set.prepare(allText);
    set.scan(allText);
    return new TextKit(set, emojiIn(allText, set));
  }

  width(text: string, size: number, bold = false): number {
    return this.set.width(text, size, "sans", bold);
  }

  draw(page: PDFPage, text: string, o: { x: number; y: number; size: number; bold?: boolean; color?: Color }): void {
    let x = o.x;
    for (const run of this.set.runs(text, "sans", o.bold ?? false)) {
      this.set.drawRun(page, run, { x, y: o.y, size: o.size, color: o.color ?? rgb(0.1, 0.1, 0.1) });
      x += run.font.widthOfTextAtSize(run.text, o.size);
    }
  }

  /** Draws `text` so its right edge is at `rightX`. */
  drawRight(page: PDFPage, text: string, o: { rightX: number; y: number; size: number; bold?: boolean; color?: Color }): void {
    this.draw(page, text, { x: o.rightX - this.width(text, o.size, o.bold), y: o.y, size: o.size, bold: o.bold, color: o.color });
  }

  /** Word-wraps to `maxWidth`; a single word wider than the line is broken by character. */
  wrap(text: string, size: number, maxWidth: number, bold = false): string[] {
    const w = (t: string) => this.width(t, size, bold);
    const out: string[] = [];
    for (const rawLine of text.split(/\r?\n/)) {
      const words = rawLine.split(/\s+/).filter(Boolean);
      let current = "";
      for (const word of words) {
        const attempt = current ? `${current} ${word}` : word;
        if (w(attempt) <= maxWidth) { current = attempt; continue; }
        if (current) out.push(current);
        if (w(word) > maxWidth) {
          let chunk = "";
          for (const ch of word) {
            if (w(chunk + ch) > maxWidth && chunk) { out.push(chunk); chunk = ch; } else chunk += ch;
          }
          current = chunk;
        } else current = word;
      }
      out.push(current);
    }
    return out.length ? out : [""];
  }

  /** Embeds the pictures for text drawn by the browser. Call once, before pdf.save(). */
  async finish(): Promise<void> {
    await this.set.flushRasters();
  }

  /** A sentence for the person when something could not be drawn, else null. */
  note(): string | null {
    const replaced = replacedCharsMessage(this.set);
    const emoji = emojiNote(this.emoji);
    return [replaced, emoji].filter(Boolean).join(" ") || null;
  }
}

const PICTO = new RegExp("\\p{Extended_Pictographic}", "u");

/** Emoji the PDF fonts cannot draw themselves (they depend on the browser's emoji font, so they may be blank). */
export function emojiIn(text: string, set?: { canDraw(ch: string): boolean }): string[] {
  const found = new Set<string>();
  for (const ch of text) if (PICTO.test(ch) && !(set && set.canDraw(ch))) found.add(ch);
  return Array.from(found);
}

/** One sentence telling the person emoji may be missing from the PDF (the Word file keeps them); null when none. */
export function emojiNote(emoji: string[]): string | null {
  if (emoji.length === 0) return null;
  return `Emoji (${emoji.slice(0, 5).join(" ")}${emoji.length > 5 ? " …" : ""}) may not show in the PDF because PDF fonts have no emoji. Remove them or use the Word file if they matter.`;
}
